import { Link, useRouterState } from "@tanstack/react-router";
import { Fragment } from "react";

type Section =
  | "home"
  | "blog"
  | "medcan"
  | "hr"
  | "matrix"
  | "benzo"
  | "opioide"
  | "terpene";

type Crumb = { to: string; label: string; section: Section };

const ROUTES: Record<string, { label: string; section: Section; parent?: string }> = {
  "/": { label: "Home", section: "home" },
  "/blog": { label: "Blog", section: "blog", parent: "/" },
  "/medcan": { label: "MedCanReview", section: "medcan", parent: "/" },
  "/terpene": { label: "Terpene", section: "terpene", parent: "/medcan" },
  "/harm-reduction": { label: "Harm Reduction", section: "hr", parent: "/" },
  "/matrix": { label: "Interaktion", section: "matrix", parent: "/harm-reduction" },
  "/benzo": { label: "Benzodiazepine", section: "benzo", parent: "/harm-reduction" },
  "/opioide": { label: "Opioide", section: "opioide", parent: "/harm-reduction" },
  "/notfall": { label: "Notfall", section: "hr", parent: "/harm-reduction" },
};

function titleize(slug: string) {
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolve(pathname: string): Crumb[] | null {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/" || clean.startsWith("/auth") || clean.startsWith("/admin")) return null;

  // Try exact match first
  let current: { to: string; label: string; section: Section; parent?: string } | null = null;
  if (ROUTES[clean]) {
    current = { to: clean, ...ROUTES[clean] };
  } else {
    // Dynamic patterns
    const segs = clean.split("/").filter(Boolean);
    if (segs[0] === "blog" && segs[1]) {
      current = { to: clean, label: titleize(segs[1]), section: "blog", parent: "/blog" };
    } else if (segs[0] === "harm-reduction" && segs[1]) {
      current = { to: clean, label: titleize(segs[1]), section: "hr", parent: "/harm-reduction" };
    } else if (segs[0] === "medcan" && segs[1]) {
      current = { to: clean, label: titleize(segs[1]), section: "medcan", parent: "/medcan" };
    } else if (segs[0] === "matrix" && segs[1]) {
      current = { to: clean, label: titleize(decodeURIComponent(segs[1])), section: "matrix", parent: "/matrix" };
    }
  }

  if (!current) return null;

  const chain: Crumb[] = [{ to: current.to, label: current.label, section: current.section }];
  let parentPath: string | undefined = current.parent;
  const guard = new Set<string>([current.to]);
  while (parentPath && !guard.has(parentPath)) {
    const r = ROUTES[parentPath];
    if (!r) break;
    guard.add(parentPath);
    chain.unshift({ to: parentPath, label: r.label, section: r.section });
    parentPath = r.parent;
  }
  return chain;
}

export function Breadcrumbs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const chain = resolve(pathname);
  if (!chain || chain.length < 2) return null;
  const active = chain[chain.length - 1];

  return (
    <nav
      aria-label="Breadcrumb"
      className="breadcrumbs"
      data-section={active.section}
    >
      <ol>
        {chain.map((c, i) => {
          const isLast = i === chain.length - 1;
          return (
            <Fragment key={c.to}>
              <li data-section={c.section}>
                {isLast ? (
                  <span aria-current="page" title={c.label}>
                    {c.label}
                  </span>
                ) : (
                  <Link to={c.to as string as never} title={c.label}>
                    {c.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li className="sep" aria-hidden="true">
                  ›
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
